import { useMemo, useState } from 'react';
import type { TreeNode } from '../lib/api.js';
import { ancestorsOf, visibleRows } from './tree.js';
import { BADGE_LEGEND, badgesFor, carriesSomething, isWorked } from './badges.js';

/**
 * The clause tree.
 *
 * The signature screen: it is what users look at most, and no other product
 * has it. Every mark is a typographic glyph — the bundle ships no image assets
 * — and every badge pairs its glyph with a colour so meaning survives
 * monochrome print and never rests on hue alone.
 *
 * The glyphs, their order and their meanings live in badges.ts, with the
 * legend that explains them at the foot of this rail.
 */

export const CHIPS = ['Protect', 'Silent', 'Broken', 'Mine', 'Unanswered', 'In a claim'] as const;
export type Chip = (typeof CHIPS)[number];

function matchesChip(node: TreeNode, chip: Chip): boolean {
  switch (chip) {
    case 'Protect':
      return node.badges.protect;
    case 'Silent':
      return node.badges.silent;
    case 'Broken':
      return node.badges.brokenRef > 0;
    case 'Mine':
      return node.badges.annotations > 0;
    case 'Unanswered':
      return node.badges.questions > 0;
    case 'In a claim':
      return node.badges.claims > 0;
  }
}

export function Rail({
  nodes,
  selected,
  onSelect,
  heat,
  onHeat,
  expanded,
  onToggle,
}: {
  nodes: TreeNode[];
  selected: string | null;
  onSelect: (number: string) => void;
  heat: boolean;
  onHeat: (on: boolean) => void;
  expanded: ReadonlySet<string>;
  onToggle: (number: string) => void;
}) {
  const [active, setActive] = useState<Chip[]>([]);
  // The full outline first. An earlier build defaulted to the attention view
  // and it read as data loss — Parts with no findings vanished entirely, and
  // "where did Part 1 go" is not a question a document tool should raise.
  // The filtered view is one click away and clearly labelled as a filter.
  const [attention, setAttention] = useState(false);

  const maxAnnotations = useMemo(
    () => Math.max(1, ...nodes.map((n) => n.badges.annotations)),
    [nodes],
  );

  /** Parts and schedules: kept in every view, so rows always have a home. */
  const isSection = (node: TreeNode): boolean =>
    node.kind === 'part' || node.kind === 'schedule';

  const attentionRows = useMemo(() => {
    if (!attention) return nodes;
    const kept = nodes.filter((n) => isSection(n) || carriesSomething(n));
    // A Part with nothing under it is noise in this view.
    return kept.filter((node, i) => {
      if (!isSection(node)) return true;
      const next = kept[i + 1];
      return next !== undefined && !isSection(next);
    });
  }, [nodes, attention]);

  const visible = useMemo(() => {
    const source = attentionRows;
    if (active.length === 0) return source;
    // Chips filter to matching nodes, plus each match's actual ancestor
    // chain for context. An earlier version kept every preceding node of any
    // shallower depth — one matching subclause dragged in dozens of
    // unrelated Parts and clauses, defeating the search the chips exist for.
    // (It also depended on the outer, unfiltered list, so toggling the
    // attention view returned a stale result.)
    const keep = new Set<string>();
    for (const node of source) {
      if (active.some((chip) => matchesChip(node, chip))) {
        keep.add(node.number);
        // Resolved against the unfiltered tree: ancestorsOf infers ancestry
        // from the nearest preceding shallower row, so walking the filtered
        // list would adopt whichever unrelated clause happened to survive it.
        for (const ancestor of ancestorsOf(nodes, node.number)) keep.add(ancestor);
      }
    }
    return source.filter((n) => keep.has(n.number));
  }, [nodes, attentionRows, active]);

  // Filtering flattens the hierarchy on purpose: a chip is a search, and
  // hiding a match inside a collapsed Part would defeat it.
  const rows = useMemo(
    () =>
      // Both the chips and the attention view are searches, so neither nests:
      // hiding a match inside a collapsed Part would defeat the point.
      active.length > 0 || attention
        ? visible.map((node, index) => ({ node, index, hasChildren: false, expanded: false }))
        : visibleRows(nodes, expanded),
    [nodes, visible, active, attention, expanded],
  );

  return (
    <div>
      {/* 10px, the mockup's rail inset, against the 6px the first row indents
          to — the header and the tree beneath it were 2px apart and read as
          one unaligned column. */}
      <div
        className="sticky top-0 z-10 border-b border-[var(--color-rule)] bg-[var(--color-paper)] px-2.5 py-2.5"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <button
            onClick={() => setAttention((on) => !on)}
            aria-pressed={attention}
            className="hit label"
            style={{ color: 'var(--color-ink)' }}
            title={
              attention
                ? 'Showing only clauses somebody has found something in'
                : 'Showing every clause in the agreement'
            }
          >
            {attention ? 'Attention' : 'Outline'} · {rows.length}
            <span style={{ color: 'var(--color-muted)' }}>
              {attention ? ` of ${nodes.length}` : ''}
            </span>
          </button>
          <button
            onClick={() => onHeat(!heat)}
            className="label hit"
            style={{ color: heat ? 'var(--color-ink)' : 'var(--color-muted)' }}
            aria-pressed={heat}
            title="Tint the tree by where the group is actually working"
          >
            Heat
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {CHIPS.map((chip) => {
            const on = active.includes(chip);
            return (
              /* Same chip as the register control in the reader: mono micro-
                 label, 4px radius, hairline — the two are the same kind of
                 thing (a way of narrowing what you are looking at) and were
                 set in two different registers. `hit` because 10px sans at
                 py-0.5 came to about 15px tall, under the 24px minimum. */
              <button
                key={chip}
                onClick={() =>
                  setActive((prev) =>
                    prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip],
                  )
                }
                aria-pressed={on}
                className="hit label rounded-[4px] border px-1.5 py-1"
                style={{
                  borderColor: on ? 'var(--color-ink)' : 'var(--color-rule)',
                  background: on ? 'var(--color-ink)' : 'transparent',
                  color: on ? 'var(--color-paper)' : 'var(--color-muted)',
                }}
              >
                {chip}
              </button>
            );
          })}
        </div>
      </div>

      <ul>
        {rows.map((row) => {
          const node = row.node;
          const isSelected = node.number === selected;
          // Cold regions are blind spots, which is a finding rather than
          // decoration: clause 19 glowing while a schedule sits ice-cold says
          // where the group has not looked.
          const density = node.badges.annotations / maxAnnotations;
          const background = isSelected
            ? 'var(--color-subtle)'
            : heat && node.badges.annotations > 0
              ? `rgba(42, 42, 40, ${(0.03 + density * 0.15).toFixed(3)})`
              : 'transparent';
          const worked = isWorked(node);

          return (
            <li key={node.number}>
              <button
                onClick={() => {
                  if (row.hasChildren) onToggle(node.number);
                  onSelect(node.number);
                }}
                aria-current={isSelected ? 'true' : undefined}
                aria-expanded={row.hasChildren ? row.expanded : undefined}
                className="hit flex w-full items-baseline gap-1.5 py-[3px] pr-2 text-left"
                style={{
                  // The 2px the edge takes is subtracted from the indent, so
                  // an untouched row and a marked one keep the same left edge
                  // and the outline does not appear to jitter as marks land.
                  paddingLeft: 6 + node.depth * 12,
                  background,
                  /*
                    The bar is the answer to "I cannot tell from the rail that
                    anyone has been here". A 10px glyph at the far end of a
                    228px row is at the opposite corner from where the eye
                    lands, and it competes with five other glyphs; a full-height
                    edge at the start of the row is legible from a scroll.
                  */
                  borderLeft: `2px solid ${worked ? 'var(--color-ink)' : 'transparent'}`,
                  // A Part carries a heavier rule than the clauses beneath it.
                  borderTop:
                    node.depth === 0 ? '1px solid var(--color-rule-strong)' : '1px solid transparent',
                }}
              >
                <span
                  aria-hidden
                  className="clause-number w-[7px] shrink-0 text-[8px]"
                  style={{ color: 'var(--color-rule-strong)' }}
                >
                  {row.hasChildren ? (row.expanded ? '▾' : '▸') : ''}
                </span>
                <span
                  className="clause-number shrink-0 text-[10px]"
                  style={{
                    // A worked clause takes the Part's weight and ink. The
                    // number is what people navigate by and quote at each
                    // other, so it is where the second cue belongs.
                    color:
                      node.depth === 0 || worked ? 'var(--color-ink)' : 'var(--color-muted)',
                    fontWeight: node.depth === 0 || worked ? 500 : 400,
                  }}
                >
                  {node.number}
                </span>
                <span
                  className="truncate text-[11px]"
                  style={{
                    // Untitled rows show their opening words instead, in the
                    // muted weight, so a snippet never reads as a title the
                    // agreement does not have.
                    color: node.heading
                      ? node.hasText || node.isContainer
                        ? 'var(--color-ink)'
                        : 'var(--color-muted)'
                      : 'var(--color-muted)',
                    fontStyle: node.heading ? undefined : 'italic',
                  }}
                  title={node.heading ?? node.snippet ?? node.number}
                >
                  {node.heading ?? node.snippet ?? ''}
                </span>
                <Badges node={node} />
              </button>
            </li>
          );
        })}
      </ul>

      <Legend />
    </div>
  );
}

/**
 * The badge legend, at the foot of the rail.
 *
 * Six glyphs are six things to have been told once, and nobody was ever told:
 * the rail shipped `△`, `∅` and `◇` with only a hover title, which a keyboard
 * user never sees and a person scanning never waits for. The mockup puts the
 * key at the bottom of the tree where it can be read in passing, and the
 * definitions behind the same (i) every other panel in this product uses.
 *
 * Sticky rather than scrolled to the end of 235 rows, because a legend you
 * have to go looking for is a legend for people who already know.
 */
function Legend() {
  const [open, setOpen] = useState(false);

  return (
    <div className="sticky bottom-0 border-t border-[var(--color-rule)] bg-[var(--color-paper)] px-2.5 py-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {BADGE_LEGEND.map((kind) => (
          <span
            key={kind.key}
            title={kind.meaning}
            className="clause-number text-[9px] whitespace-nowrap"
            style={{ color: 'var(--color-muted)' }}
          >
            <span style={{ color: kind.colour }}>{kind.glyph}</span> {kind.name}
          </span>
        ))}
        <button
          className="info-button"
          aria-expanded={open}
          aria-label="What do these badges mean?"
          onClick={() => setOpen((shown) => !shown)}
        >
          i
        </button>
      </div>

      {open ? (
        <dl className="mt-2 border-t border-[var(--color-rule)] pt-2">
          {BADGE_LEGEND.map((kind) => (
            <div key={kind.key} className="mb-1.5 flex gap-2 last:mb-0">
              <dt
                className="clause-number w-[72px] shrink-0 text-[9px]"
                style={{ color: kind.colour }}
              >
                {kind.glyph} {kind.name}
              </dt>
              <dd className="m-0 min-w-0 flex-1 text-[10px] leading-snug text-[var(--color-muted)]">
                {kind.meaning}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

/**
 * The glyphs at the end of a row, in the order the legend below lists them.
 *
 * Nothing at all when there is nothing to say. A reserved lane would align the
 * runs, but it would also take 54px off the heading on all 235 rows to do it,
 * and `ml-auto` already flushes them to the same right edge — which is the
 * edge the eye actually runs down.
 */
function Badges({ node }: { node: TreeNode }) {
  const marks = badgesFor(node);
  if (marks.length === 0) return null;

  return (
    <span className="ml-auto flex shrink-0 items-baseline justify-end gap-1">
      {marks.map((mark) => (
        <span
          key={mark.key}
          title={mark.label}
          aria-label={mark.label}
          className="clause-number text-[10px]"
          style={{ color: mark.colour }}
        >
          {mark.text}
        </span>
      ))}
    </span>
  );
}
